<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="800px"
        transition="slide-y-transition"
        eager
        :persistent="actionLoading"
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Edycja zbiorcza komponentów
                    </template>
                </h1>
            </v-card-title>

            <v-data-table
                :headers="headers"
                :items="productComponents"
                :mobile-breakpoint="0"
                hide-default-footer
                disable-pagination
                name="productComponentsData"
            >
                <template #item.calculationProductName="{item}">
                    {{ item.calculationProductName }}
                </template>
                <template #item.componentName="{item}">
                    {{ item.component.name }}
                </template>
                <template #item.parts="{item}">
                    <v-text-field
                        :value="item.parts"
                        type="number"
                        min="1"
                        dense
                        outlined
                        hide-details
                        :error="partsErrors(item.index)"
                        data-sentry-label="Zmiana użytków"
                        @change="changeParts(item, $event)"
                    />
                </template>
                <template #item.amount="{item}">
                    {{ item.amount }}
                </template>
                <template #item.calculationImpositionId="{item}">
                    <v-select
                        :value="item.calculationImpositionIndex"
                        :items="impositions"
                        item-value="index"
                        outlined
                        dense
                        clearable
                        hide-details
                        return-object
                        data-sentry-label="Zmiana montażu"
                        @change="changeImposition(item, $event)"
                    >
                        <template #append-item>
                            <v-divider class="mb-2" />
                            <v-list-item
                                ripple
                                data-sentry-label="Przypisanie komponentu do nowego montażu"
                                @click="assignComponentToNewImposition(item)"
                            >
                                Dodaj nowy
                            </v-list-item>
                        </template>
                        <template slot="selection" slot-scope="data">
                            Montaż {{ data.item.index + 1 }}
                        </template>

                        <template slot="item" slot-scope="data">
                            Montaż {{ data.item.index + 1 }}
                        </template>
                    </v-select>
                </template>
            </v-data-table>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    :disabled="actionLoading"
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    :loading="actionLoading"
                    data-sentry-label="Zapisz"
                    @click="onSave"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import parse from '@/mixins/parse'
import { isEqual } from 'lodash'

export default {
    name: 'ManageComponentsDialog',
    mixins: [ parse ],
    props: {
        isOpen: {
            type: Boolean
        },
        productComponentsData: {
            type: Array,
            default: null
        },
        impositionsData: {
            type: Array,
            default: null
        },
        actionLoading: {
            type: Boolean
        }
    },
    data() {
        return {
            impositions: [],
            productComponents: [],
            initialProductComponents: [],
            headers: [
                {
                    text: 'Nazwa produktu',
                    value: 'calculationProductName',
                    sortable: false,
                },
                {
                    text: 'Komponent',
                    value: 'componentName',
                    sortable: false,
                },
                {
                    text: 'Ilość użytków',
                    value: 'parts',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    sortable: false,
                    align: 'center',
                    width: 150
                },
                {
                    text: 'Montaż',
                    value: 'calculationImpositionId',
                    sortable: false,
                    width: 200
                }
            ]
        }
    },
    validations() {
        const rules = {
            productComponents: {
                $each: {
                    parts: {
                        required: (value, productComponent) => {
                            if (productComponent.calculationImpositionId) {
                                return !!value
                            } else {
                                return true
                            }
                        }
                    }
                }
            }
        }

        return rules
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.impositions = this.impositionsData.map((item, index) => ({
                    ...item,
                    index
                }))

                this.productComponentsData.forEach(product => {
                    product.calculationProductComponents.forEach((component) => {
                        let calculationImpositionIndex = null

                        const indexedImposition = this.impositions.find(imposition => {
                            return imposition.calculationProductComponentIds.includes(component.id)
                        })

                        if (indexedImposition) calculationImpositionIndex = indexedImposition.index

                        this.productComponents.push({
                            ...component,
                            amount: product.amount,
                            calculationImpositionIndex: calculationImpositionIndex
                        })
                    })
                })

                this.productComponents = this.productComponents.map((component, index) => ({
                    ...component,
                    index
                }))
                this.initialProductComponents = JSON.parse(JSON.stringify(this.productComponents))
            } else {
                setTimeout(() => {
                    this.impositions = []
                    this.productComponents = []
                    this.$v.$reset()
                }, 500)
            }
        }
    },
    methods: {
        changeParts(component, value) {
            component.parts = this.parseEmptyStringToNull(value)
        },
        changeImposition(component, imposition) {
            if (imposition) {
                component.calculationImpositionId = imposition.id
                component.calculationImpositionIndex = imposition.index
            } else {
                component.calculationImpositionId = null
                component.calculationImpositionIndex = null
            }
        },
        assignComponentToNewImposition() {
            const imposition = {
                calculationProductComponentIds: [],
                calculationProductComponents: [],
                calculationImpositionTasks: [],
                index: this.impositions.length
            }

            this.impositions.push(imposition)
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.impositions.forEach(imposition => {
                    if (typeof imposition.id === 'undefined') {
                        this.$store.dispatch('calculations/addImposition', imposition)
                    }
                })

                this.productComponents.forEach(component => {
                    this.$store.dispatch('calculations/setProductComponent', component)
                })

                this.impositions.forEach(imposition => {
                    imposition.calculationProductComponentIds = this.productComponents.filter(component => {

                        return component.calculationImpositionIndex === imposition.index
                    })
                        .map(item => item.id)
                })
                this.productComponents.forEach((component, index) => {
                    // const componentIndex = this.initialProductComponents.findIndex(item => item.id === component.id)

                    // if (!isEqual(component, this.initialProductComponents[componentIndex])) {
                    //     this.impositions.forEach(imposition => {
                    //         const calculationProductComponentIndex = imposition.calculationProductComponents.findIndex(componentItem => componentItem.id === component.id)
                    //         if (calculationProductComponentIndex > -1) {
                    //             imposition.componentsChanged = true
                    //         }
                    //     })
                    // }
                    if (!isEqual(component, this.initialProductComponents[index])) {
                        if (null === component.calculationImpositionId) {
                            // index z listy skopiowanej - nowa impozycja
                            const impositionPrevIndex = this.impositions.findIndex(imposition => imposition.id === this.initialProductComponents[index].calculationImpositionId)

                            this.impositions[impositionPrevIndex].componentsChanged = true
                        }
                        if (component.calculationImpositionId) {
                            // index z oryginalnej listy
                            // if 
                            const impositionPrevIndex = this.impositions.findIndex(imposition => imposition.id === this.initialProductComponents[index].calculationImpositionId)

                            const impositionIndex = this.impositions.findIndex(imposition => imposition.id === component.calculationImpositionId)

                            if (impositionPrevIndex !== -1) {
                                this.impositions[impositionPrevIndex].componentsChanged = true
                            }
                            if (impositionIndex !== -1) {
                                this.impositions[impositionIndex].componentsChanged = true
                            }
                        }

                    }
                })
                this.$emit('on-save', this.impositions)
            }
        },
        onClose() {
            if (!this.actionLoading) {
                this.$emit('on-close')
            }
        },
        partsErrors(index) {
            if (!this.$v.productComponents.$each[index].parts.$dirty) return

            return !this.$v.productComponents.$each[index].parts.required
        },
    }
}
</script>
